import { useState } from 'react'
import { getAntdComponentMarker } from './antdComponentMarker'
import { useComponentOutline, type ComponentOutlineTarget } from './useComponentOutline'

const baseLabelStyle = {
  appearance: 'none',
  borderRadius: '2px',
  cursor: 'copy',
  display: 'none',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: '11px',
  fontWeight: '600',
  lineHeight: '16px',
  padding: '0 4px',
  pointerEvents: 'auto',
  position: 'fixed',
  whiteSpace: 'nowrap',
  zIndex: '2147483647',
}

const dataComponentLabelStyle = {
  ...baseLabelStyle,
  background: '#fff1f0',
  border: '1px solid #ff4d4f',
  color: '#cf1322',
}

const antdComponentLabelStyle = {
  ...baseLabelStyle,
  background: '#e6fffb',
  border: '1px solid #13c2c2',
  color: '#08979c',
}

const antdIconLabelStyle = {
  ...baseLabelStyle,
  background: '#595959',
  border: '1px solid #8c8c8c',
  color: '#ffffff',
}

const permissionOutlineLabelStyle = {
  ...baseLabelStyle,
  background: '#fff7e6',
  border: '1px solid #fa8c16',
  color: '#ad4e00',
  lineHeight: '18px',
  maxWidth: '360px',
  padding: '4px 6px',
  textAlign: 'left',
  whiteSpace: 'pre-line',
}

function resolveDataComponentTarget(target: Element): ComponentOutlineTarget | null {
  const element = target.closest<HTMLElement>('[data-c]')
  const componentName = element?.getAttribute('data-c')

  return element && componentName ? { element, componentName } : null
}

function resolveAntdComponentTarget(target: Element): ComponentOutlineTarget | null {
  let element: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement

  while (element) {
    const marker = getAntdComponentMarker(element)
    if (marker && element.classList.contains(marker.className)) {
      return {
        element,
        componentName: marker.componentName,
        labelStyle: marker.className === 'anticon' ? antdIconLabelStyle : antdComponentLabelStyle,
      }
    }
    element = element.parentElement
  }

  return null
}

function resolvePermissionTarget(target: Element): ComponentOutlineTarget | null {
  const marker = target.closest<HTMLElement>('[data-permission-debug-id]')
  if (!marker) return null

  const element = marker.firstElementChild instanceof HTMLElement ? marker.firstElementChild : marker
  const action = marker.dataset.permissionDebugAction ?? '未命名操作'
  const location = marker.dataset.permissionDebugLocation ?? '未标注业务位置'
  const permission = marker.dataset.permissionDebugPermission ?? '显式条件'

  return {
    element,
    componentName: `操作：${action}\n位置：${location}\n权限：${permission}`,
    labelStyle: permissionOutlineLabelStyle,
  }
}

function positionDataComponentLabel(label: HTMLButtonElement, target: ComponentOutlineTarget): void {
  label.style.display = 'block'
  label.style.visibility = 'hidden'
  const rect = target.element.getBoundingClientRect()
  const top = rect.top >= label.offsetHeight ? rect.top : rect.bottom
  const left = Math.min(Math.max(rect.left, 0), Math.max(0, window.innerWidth - label.offsetWidth))
  label.style.left = `${left}px`
  label.style.top = `${top}px`
  label.style.transform = rect.top >= label.offsetHeight ? 'translateY(-100%)' : 'none'
  label.style.visibility = 'visible'
}

function positionAntdComponentLabel(label: HTMLButtonElement, target: ComponentOutlineTarget): void {
  label.style.display = 'block'
  label.style.visibility = 'hidden'
  const rect = target.element.getBoundingClientRect()
  const left = Math.min(Math.max(rect.left, 0), Math.max(0, window.innerWidth - label.offsetWidth))
  label.style.left = `${left}px`
  label.style.top = `${rect.bottom}px`
  label.style.transform = 'none'
  label.style.visibility = 'visible'
}

export type ComponentOutlineControlsProps = {
  componentOutlineEnabled: boolean
  setComponentOutlineEnabled: (enabled: boolean) => void
  antdComponentOutlineEnabled: boolean
  setAntdComponentOutlineEnabled: (enabled: boolean) => void
  permissionOutlineEnabled: boolean
  setPermissionOutlineEnabled: (enabled: boolean) => void
}

export function useComponentOutlineControls(): ComponentOutlineControlsProps {
  const [componentOutlineEnabled, setComponentOutlineEnabled] = useState(false)
  const [antdComponentOutlineEnabled, setAntdComponentOutlineEnabled] = useState(false)
  const [permissionOutlineEnabled, setPermissionOutlineEnabled] = useState(false)

  useComponentOutline({
    enabled: componentOutlineEnabled,
    bodyAttribute: 'data-dev-component-outline',
    labelAttribute: 'data-dev-component-outline-label',
    labelStyle: dataComponentLabelStyle,
    resolveTarget: resolveDataComponentTarget,
    positionLabel: positionDataComponentLabel,
  })
  useComponentOutline({
    enabled: antdComponentOutlineEnabled,
    bodyAttribute: 'data-dev-antd-component-outline',
    labelAttribute: 'data-dev-antd-component-outline-label',
    labelStyle: antdComponentLabelStyle,
    resolveTarget: resolveAntdComponentTarget,
    positionLabel: positionAntdComponentLabel,
  })
  useComponentOutline({
    enabled: permissionOutlineEnabled,
    bodyAttribute: 'data-dev-permission-outline',
    labelAttribute: 'data-dev-permission-outline-label',
    labelStyle: permissionOutlineLabelStyle,
    resolveTarget: resolvePermissionTarget,
    positionLabel: positionAntdComponentLabel,
  })

  return {
    componentOutlineEnabled,
    setComponentOutlineEnabled,
    antdComponentOutlineEnabled,
    setAntdComponentOutlineEnabled,
    permissionOutlineEnabled,
    setPermissionOutlineEnabled,
  }
}
